interface TapeCell {
  value?: string | number; // shown inside the cell
  color?: 'violet' | 'sky' | 'sun' | 'coral';
}

interface TapeRow {
  label: string;
  cells: TapeCell[];
}

interface Props {
  rows: TapeRow[];
  title?: string;
  caption?: string;
}

const colorMap = {
  violet: 'bg-violet text-white',
  sky: 'bg-sky text-white',
  sun: 'bg-sun text-ink',
  coral: 'bg-coral text-white',
} as const;

// Tape diagram — the Common Core signature visual for ratios and rates.
// Used throughout Chapter 5.
export default function TapeDiagramViz({ rows, title = 'Tape diagram', caption }: Props) {
  // Pad all rows to the same cell count so bars line up visually.
  const maxCells = Math.max(0, ...rows.map((r) => r.cells.length));

  return (
    <div className="card p-5 space-y-3">
      <h3 className="font-display text-lg">{title}</h3>
      <div className="space-y-2">
        {rows.map((row, i) => {
          const defaultColor = i % 2 === 0 ? 'violet' : 'sky';
          return (
            <div key={i} className="flex items-center gap-3">
              <div className="w-20 shrink-0 text-xs text-ink/70 font-bold text-right">
                {row.label}
              </div>
              <div className="flex-1 flex gap-1">
                {row.cells.map((cell, j) => (
                  <div
                    key={j}
                    className={`flex-1 math-display font-bold text-center py-2.5 rounded-md text-sm ${
                      colorMap[cell.color ?? defaultColor]
                    }`}
                  >
                    {cell.value ?? ''}
                  </div>
                ))}
                {/* Pad with invisible cells so rows align */}
                {Array.from({ length: maxCells - row.cells.length }).map((_, j) => (
                  <div key={`pad-${j}`} className="flex-1" />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {caption && <div className="text-sm text-ink/70">{caption}</div>}
    </div>
  );
}
